import { css } from '@inter-ikea-kompis/web-component';
import PopoverAlignmentEnum from '../../enums/PopoverAlignmentEnum.js';
import PopoverDirectionEnum from '../../enums/PopoverDirectionEnum.js';
export default class PopoverPositionerStyle {
    /**
     * Returns styles.
     *
     * @param options Options object.
     * @returns Css code.
     */
    static getStyle(options) {
        const { motion, spacing } = options.theme;
        return css `
			:host {
				display: block;
				pointer-events: none;
				direction: unset;
				z-index: ${options.zIndex};
			}
			* {
				z-index: inherit;
			}
			.boundingContainer {
				direction: ltr;
				pointer-events: none;
				position: fixed;
				display: flex;
				overflow: hidden;
				top: 0;
				left: 0;
				bottom: 0;
				right: 0;
			}
			.alignmentContainer {
				max-width: 100%;
				max-height: 100%;
				pointer-events: none;
				display: flex;
				flex-direction: inherit;
				padding: ${spacing.space75 / 16}rem;
				animation-name: ${options.visible ? 'animationIn' : 'animationOut'};
				animation-fill-mode: ${options.visible ? 'backwards' : 'forwards'};
				animation-duration: ${motion.duration.small};
			}
			.balloon {
				max-width: 100%;
				max-height: 100%;
				display: flex;
				pointer-events: ${options.visible ? 'auto' : 'none'};
				animation-duration: ${motion.duration.small};
			}
			${this.getBoundingContainer(options)}
			${this.getAlignmentContainer(options)}
			${this.getWiggleAnimation(options)}
			${this.getAnimations(options)}
		`;
    }
    /**
     * Retrieves flex direction for bounding container.
     *
     * @param options Options.
     */
    static getAlignmentContainer(options) {
        switch (options.direction) {
            case PopoverDirectionEnum.bottom:
            case PopoverDirectionEnum.top:
                switch (options.alignment) {
                    case PopoverAlignmentEnum.end:
                        return css `
							.boundingContainer {
								justify-content: flex-start;
							}
							.alignmentContainer {
								padding-right: 0;
								justify-content: flex-end;
								min-width: ${options.containerRect.right}px;
							}
						`;
                    case PopoverAlignmentEnum.start:
                        return css `
							.boundingContainer {
								justify-content: flex-end;
							}
							.alignmentContainer {
								padding-left: 0;
								justify-content: flex-start;
								min-width: ${options.innerWidth - options.containerRect.left}px;
							}
						`;
                    case PopoverAlignmentEnum.center: {
                        const middleOfRect = options.containerRect.left + options.containerRect.width / 2;
                        const middleOfViewport = options.innerWidth / 2;
                        const diff = middleOfViewport - middleOfRect;
                        if (diff > 0) {
                            return css `
								.boundingContainer {
									justify-content: flex-start;
								}
								.alignmentContainer {
									justify-content: center;
									min-width: ${options.innerWidth - diff * 2}px;
								}
							`;
                        }
                        return css `
							.boundingContainer {
								justify-content: flex-end;
							}
							.alignmentContainer {
								justify-content: center;
								min-width: ${options.innerWidth + diff * 2}px;
							}
						`;
                    }
                    default:
                        break;
                }
                return css `
					.alignmentContainer {
						display: flex;
					}
				`;
            case PopoverDirectionEnum.leading:
            case PopoverDirectionEnum.trailing:
                switch (options.alignment) {
                    case PopoverAlignmentEnum.end:
                        return css `
							.boundingContainer {
								justify-content: flex-start;
							}
							.alignmentContainer {
								padding-bottom: 0;
								justify-content: flex-end;
								height: ${options.containerRect.bottom}px;
							}
						`;
                    case PopoverAlignmentEnum.start:
                        return css `
							.boundingContainer {
								justify-content: flex-end;
							}
							.alignmentContainer {
								padding-top: 0;
								justify-content: flex-start;
								height: ${options.innerHeight - options.containerRect.top}px;
							}
						`;
                    case PopoverAlignmentEnum.center: {
                        const middleOfRect = options.containerRect.top + options.containerRect.height / 2;
                        const middleOfViewport = options.innerHeight / 2;
                        const diff = middleOfViewport - middleOfRect;
                        if (diff > 0) {
                            return css `
								.boundingContainer {
									justify-content: flex-start;
								}
								.alignmentContainer {
									justify-content: center;
									height: ${options.innerHeight - diff * 2}px;
								}
							`;
                        }
                        return css `
							.boundingContainer {
								justify-content: flex-end;
							}
							.alignmentContainer {
								justify-content: center;
								height: ${options.innerHeight + diff * 2}px;
							}
						`;
                    }
                    default:
                        break;
                }
                return css `
					.alignmentContainer {
						display: flex;
					}
				`;
        }
    }
    /**
     * Sets alignment style for popover.
     *
     * @param options Options object.
     */
    static getBoundingContainer(options) {
        switch (options.direction) {
            case PopoverDirectionEnum.top:
                return css `
					.boundingContainer {
						flex-direction: row;
						align-items: flex-end;
						bottom: ${options.innerHeight - options.containerRect.top}px;
					}
				`;
            case PopoverDirectionEnum.bottom:
                return css `
					.boundingContainer {
						flex-direction: row;
						align-items: flex-start;
						top: ${options.containerRect.bottom}px;
					}
				`;
            case PopoverDirectionEnum.leading:
                return css `
					.boundingContainer {
						flex-direction: column;
						align-items: flex-end;
						right: ${options.innerWidth - options.containerRect.left}px;
					}
				`;
            case PopoverDirectionEnum.trailing:
                return css `
					.boundingContainer {
						flex-direction: column;
						align-items: flex-start;
						left: ${options.containerRect.right}px;
					}
				`;
        }
    }
    /**
     * Wiggle animation.
     *
     * @param options Options object.
     * @returns Animation css.
     */
    static getAnimations(options) {
        const map = {
            [PopoverDirectionEnum.top]: 'translateY(10px)',
            [PopoverDirectionEnum.bottom]: 'translateY(-10px)',
            [PopoverDirectionEnum.leading]: 'translateX(10px)',
            [PopoverDirectionEnum.trailing]: 'translateX(-10px)'
        };
        return css `
			@keyframes animationIn {
				from {
					transform: ${map[options.direction]};
					opacity: 0;
				}
				to {
					transform: translate(0);
					opacity: 1;
				}
			}
			@keyframes animationOut {
				from {
					opacity: 1;
				}
				to {
					opacity: 0;
				}
			}
		`;
    }
    /**
     * Wiggle animation.
     *
     * @param options Options object.
     * @returns Animation css.
     */
    static getWiggleAnimation(options) {
        if (!options.wiggle) {
            return '';
        }
        return css `
			@keyframes wiggle {
				6% {
					transform: rotate(3deg);
				}
				12% {
					transform: rotate(-3deg);
				}
				18% {
					transform: rotate(0deg);
				}
			}
			.balloon {
				animation-name: wiggle;
				animation-iteration-count: 10;
				animation-delay: 0.5s;
				animation-duration: 3s;
				transform-origin: ${this.getWiggleTransformOrigin(options)};
			}
		`;
    }
    static getWiggleTransformOrigin(options) {
        let alignment = '';
        switch (options.alignment) {
            case PopoverAlignmentEnum.start:
                alignment = '100%';
                break;
            case PopoverAlignmentEnum.center:
                alignment = 'center';
                break;
            case PopoverAlignmentEnum.end:
                alignment = '0%';
                break;
        }
        switch (options.direction) {
            case PopoverDirectionEnum.top:
                return `${alignment} bottom`;
            case PopoverDirectionEnum.bottom:
                return `${alignment} top`;
            case PopoverDirectionEnum.leading:
                return `right ${alignment}`;
            case PopoverDirectionEnum.trailing:
                return `left ${alignment}`;
        }
    }
}
//# sourceMappingURL=PopoverPositionerStyle.js.map